export enum CellType {
  FLOOR = 0,
  WALL = 1,
  BLOCK = 2,
  EXIT = 5,
}

export enum BombState {
  PLACED = 'PLACED',
  ARMED = 'ARMED',
  EXPLODING = 'EXPLODING',
  REMOVED = 'REMOVED',
}

export enum PowerUpType {
  EXTRA_BOMB = 'extra_bomb',
  FIRE_UP = 'fire_up',
  SPEED_UP = 'speed_up',
  BOMB_KICK = 'bomb_kick',
  REMOTE_BOMB = 'remote_bomb',
  SHIELD = 'shield',
  EXTRA_LIFE = 'extra_life',
  FIRE_IMMUNITY = 'fire_immunity',
  WALL_PASS = 'wall_pass',
  BOMB_PASS = 'bomb_pass',
}

export enum EnemyType {
  BASIC = 'basic',
  FAST = 'fast',
  HUNTER = 'hunter',
  SMART = 'smart',
  BOMBER = 'bomber',
  BOSS = 'boss',
}

export enum GameMode {
  CAMPAIGN = 'campaign',
  ARCADE = 'arcade',
  SURVIVAL = 'survival',
  MULTIPLAYER = 'multiplayer',
}

export interface Vec2 {
  x: number;
  y: number;
}

export interface PlayerStats {
  maxBombs: number;
  blastRadius: number;
  speedMult: number;
  lives: number;
  activePowerUps: Set<PowerUpType>;
  hasShield: boolean;
  hasBombKick: boolean;
  hasRemoteBomb: boolean;
  hasWallPass: boolean;
  hasBombPass: boolean;
  fireImmunityTimer: number;
}

export interface BombData {
  id: number;
  col: number;
  row: number;
  radius: number;
  ownerId: number;
  state: BombState;
  timer: number;
  hasKick: boolean;
  kickDirX: number;
  kickDirY: number;
  remote: boolean;
}

export interface ExplosionCell {
  col: number;
  row: number;
  type: 'center' | 'h' | 'v' | 'tip_left' | 'tip_right' | 'tip_up' | 'tip_down';
  timer: number;
}

export interface LevelConfig {
  world: number;
  level: number;
  seed: number;
  enemyCount: number;
  enemyTypes: EnemyType[];
  timeLimit: number;
  blockDensity: number;
  powerupChance: number;
  bossLevel: boolean;
}

export interface WorldTheme {
  floorColor: number;
  wallColor: number;
  blockColor: number;
  bgColor: number;
  accentColor: number;
  name: string;
}

export interface SaveData {
  version: number;
  highScore: number;
  maxLevel: number;
  unlockedLevels: number[];
  settings: Settings;
  stats: GameStats;
}

export interface Settings {
  theme: 'cat' | 'penguin' | 'walker';
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  fullscreen: boolean;
  quality: 'low' | 'medium' | 'high';
  language: 'ro' | 'en';
  screenShake: boolean;
  particleDensity: number;
  controls: ControlMap;
}

export interface ControlMap {
  p1Up: string;
  p1Down: string;
  p1Left: string;
  p1Right: string;
  p1Bomb: string;
  p2Up: string;
  p2Down: string;
  p2Left: string;
  p2Right: string;
  p2Bomb: string;
}

export interface GameStats {
  totalGames: number;
  totalKills: number;
  totalBombs: number;
  highestLevel: number;
}

export interface EnemyData {
  type: EnemyType;
  col: number;
  row: number;
  hp: number;
  speed: number;
}

export type Grid = CellType[][];

export type GameEventMap = {
  'bomb:placed': { col: number; row: number; id: number };
  'bomb:exploded': { id: number; cells: ExplosionCell[] };
  'block:destroyed': { col: number; row: number };
  'powerup:collected': { type: PowerUpType; playerId: number };
  'enemy:killed': { type: EnemyType; col: number; row: number };
  'player:died': { playerId: number };
  'player:respawned': { playerId: number };
  'level:complete': { score: number; timeBonus: number };
  'game:over': { score: number; level: number };
  'exit:opened': Record<string, never>;
  'score:changed': { score: number; delta: number };
};
